import React, { useState, useCallback, useEffect, useRef } from 'react';
import { AuthenticatedTemplate, UnauthenticatedTemplate } from '@azure/msal-react';
import { useAuth } from './auth/useAuth';
import { DEV_BYPASS_AUTH } from './auth/authFetch';
import ChatPhase from './components/ChatPhase';
import SummaryPhase from './components/SummaryPhase';
import CanvasPhase from './components/CanvasPhase';
import ProcessEditChat from './components/ProcessEditChat';
import TaskEditChat from './components/TaskEditChat';
import MappingLoading from './components/MappingLoading';
import MappingResults from './components/MappingResults';
import { apiFetch, API_BASE, postJson } from './apiClient';

const PHASES = {
  INPUT: 'input',
  CONVERSATION: 'conversation',
  SUMMARY: 'summary',
  PROCESS_CANVAS: 'process_canvas',
  MAPPING_LOADING: 'mapping_loading',
  MAPPING_RESULTS: 'mapping_results',
  CANVAS_FINAL: 'canvas_final',
};

// Pure derivation of the canvas view from process + mapping. Not persisted —
// recomputed on load to keep the saved conversation blob small.
function mergeProcessData(processData, mappingData) {
  if (!processData) return null;
  if (!mappingData?.mappings) return processData;
  const enrichedSteps = processData.steps.map((step) => {
    const m = mappingData.mappings.find((mp) => mp.stepId === step.id);
    if (!m) return step;
    return {
      ...step,
      primaryCapability: m.primaryCapability,
      toolRecommendation: m.toolRecommendation,
      toolDisplayName: m.toolDisplayName,
      toolDescription: m.toolDescription,
      caseType: m.caseType,
      caseTypeLabel: m.caseTypeLabel,
      automationPotential: m.automationPotential,
      liteAgentFit: m.liteAgentFit,
      level: m.level,
      liteAgentDataBridge: m.liteAgentDataBridge,
    };
  });
  return {
    ...processData,
    steps: enrichedSteps,
    overallSummary: {
      ...processData.overallSummary,
      liteAgentCandidates: mappingData.summary?.liteAgentCandidates || 0,
    },
  };
}

function LoginPage() {
  const { login } = useAuth();
  return (
    <div style={{
      minHeight: '100vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '2rem',
      background: '#f5f7fa',
    }}>
      <div style={{
        background: 'white',
        borderRadius: '8px',
        padding: '3rem 4rem',
        maxWidth: '500px',
        width: '100%',
        textAlign: 'center',
        boxShadow: '0 4px 16px rgba(0, 0, 0, 0.1)',
      }}>
        <h1 style={{ fontSize: '1.75rem', marginBottom: '0.5rem' }}>ProcessIntelligence</h1>
        <p style={{ color: '#666', marginBottom: '2rem' }}>
          Prozess-Intelligenz für Ihre Organisation
        </p>
        <h2 style={{ fontSize: '1.25rem', marginBottom: '0.5rem' }}>Anmelden</h2>
        <p style={{ color: '#888', marginBottom: '1.5rem' }}>
          Melden Sie sich mit Ihrem Azure-Konto an, um fortzufahren.
        </p>
        <button
          onClick={() => login()}
          style={{
            padding: '0.75rem 2rem',
            fontSize: '1rem',
            background: '#2E7C99',
            color: '#fff',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer',
          }}
        >
          Mit Azure AD anmelden
        </button>
      </div>
    </div>
  );
}

export default function App() {
  if (DEV_BYPASS_AUTH) return <AppContent />;
  return (
    <>
      <UnauthenticatedTemplate>
        <LoginPage />
      </UnauthenticatedTemplate>
      <AuthenticatedTemplate>
        <AppContent />
      </AuthenticatedTemplate>
    </>
  );
}

function AppContent() {
  const { email } = useAuth();
  const auth = { status: 'ok', user: { email } };
  const [phase, setPhase] = useState(PHASES.INPUT);
  const [selectedHA, setSelectedHA] = useState('');
  const [departments, setDepartments] = useState([]);

  const [messages, setMessages] = useState([]);
  const [summaryData, setSummaryData] = useState(null);
  const [editingTask, setEditingTask] = useState(false); // "Aufgabe anpassen" panel

  const [processData, setProcessData] = useState(null);
  const [mappingData, setMappingData] = useState(null);
  const [mergedProcessData, setMergedProcessData] = useState(null);

  const [savedProcessId, setSavedProcessId] = useState(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const [generateError, setGenerateError] = useState(null);
  const [toastMessage, setToastMessage] = useState(null);
  const [exportState, setExportState] = useState('idle'); // 'idle' | 'loading' | 'done'

  const [conversationId, setConversationId] = useState(null);
  const saveTimerRef = useRef(null);
  const creatingConversationRef = useRef(false);

  useEffect(() => {
    apiFetch(`${API_BASE}/api/departments`)
      .then((r) => r.json())
      .then(setDepartments)
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (auth.status !== 'ok' || !auth.user?.email) return;

    const hasPayload =
      messages.length > 0 ||
      summaryData ||
      processData ||
      phase !== PHASES.INPUT;

    if (!hasPayload) return;

    if (saveTimerRef.current) clearTimeout(saveTimerRef.current);

    saveTimerRef.current = setTimeout(async () => {
      const state = {
        phase,
        selectedHA,
        messages,
        summaryData,
        processData,
        mappingData,
        // mergedProcessData intentionally omitted — derived from processData +
        // mappingData and recomputed on load via mergeProcessData().
        savedProcessId,
      };
      try {
        if (conversationId) {
          await apiFetch(`${API_BASE}/api/conversations/${conversationId}`, {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ state }),
          });
        } else if (!creatingConversationRef.current) {
          creatingConversationRef.current = true;
          const r = await apiFetch(`${API_BASE}/api/conversations`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ state }),
          });
          creatingConversationRef.current = false;
          if (r.ok) {
            const { id } = await r.json();
            setConversationId(id);
          }
        }
      } catch (e) {
        console.error('Conversation save failed', e);
        creatingConversationRef.current = false;
      }
    }, 2000);

    return () => {
      if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    };
  }, [
    auth.status,
    auth.user?.email,
    conversationId,
    phase,
    selectedHA,
    messages,
    summaryData,
    processData,
    mappingData,
    savedProcessId,
  ]);

  const { logout: handleLogout } = useAuth();

  const handleStartConversation = useCallback((initialMessages) => {
    setMessages(initialMessages);
    setPhase(PHASES.CONVERSATION);
  }, []);

  const handleSummaryReady = useCallback((summary, allMessages) => {
    setSummaryData(summary);
    setMessages(allMessages);
    setEditingTask(false);
    setPhase(PHASES.SUMMARY);
  }, []);

  // "Anpassen" now opens the task-edit panel on the right (instead of going
  // back to the conversation).
  const handleEditSummary = useCallback(() => {
    setEditingTask(true);
  }, []);

  // Apply a task-summary change proposed by TaskEditChat (full updated summary).
  const handleApplyTaskChanges = useCallback((updatedSummary) => {
    if (updatedSummary && typeof updatedSummary === 'object') {
      setSummaryData(prev => ({ ...prev, ...updatedSummary }));
    }
  }, []);

  const handleConfirmSummary = useCallback(async () => {
    setIsGenerating(true);
    setGenerateError(null);
    try {
      const data = await postJson('/api/generate', {
        summary: summaryData,
        departmentName: selectedHA || undefined,
      });
      setProcessData(data);
      setIsGenerating(false);
      setPhase(PHASES.PROCESS_CANVAS);
    } catch (err) {
      console.error('Generate error:', err);
      setIsGenerating(false);
      setGenerateError(err.message);
      setPhase(PHASES.SUMMARY);
    }
  }, [summaryData, selectedHA]);

  // V02: Apply process edit changes from AI proposal
  const handleApplyProcessChanges = useCallback((changes, updatedSummaryText) => {
    setProcessData(prev => {
      if (!prev) return prev;
      let steps = [...prev.steps];
      let connections = [...(prev.connections || [])];

      for (const change of changes) {
        switch (change.action) {
          case 'add': {
            const afterIdx = steps.findIndex(s => s.id === change.afterStepId);
            const insertIdx = afterIdx >= 0 ? afterIdx + 1 : steps.length;
            const newId = change.stepId || `step-new-${Date.now()}`;
            const newStep = {
              id: newId,
              name: change.newName || 'Neuer Schritt',
              description: change.newDescription || '',
              type: change.newType || 'transform',
              estimatedEffort: 'medium',
              isBottleneck: false,
              agentReady: false,
              agentReadyReason: '',
            };
            steps.splice(insertIdx, 0, newStep);
            // Fix connections: if afterStep connected to something, insert new step in between
            if (afterIdx >= 0) {
              const afterId = steps[afterIdx]?.id;
              const nextId = steps[insertIdx + 1]?.id;
              if (afterId && nextId) {
                connections = connections.filter(c => !(c.from === afterId && c.to === nextId));
                connections.push({ from: afterId, to: newId });
                connections.push({ from: newId, to: nextId });
              } else if (afterId) {
                connections.push({ from: afterId, to: newId });
              }
            }
            break;
          }
          case 'remove': {
            const removeIdx = steps.findIndex(s => s.id === change.stepId);
            if (removeIdx >= 0) {
              const removedId = change.stepId;
              // Find incoming and outgoing connections
              const incoming = connections.filter(c => c.to === removedId).map(c => c.from);
              const outgoing = connections.filter(c => c.from === removedId).map(c => c.to);
              connections = connections.filter(c => c.from !== removedId && c.to !== removedId);
              // Reconnect: each incoming to each outgoing
              for (const fromId of incoming) {
                for (const toId of outgoing) {
                  connections.push({ from: fromId, to: toId });
                }
              }
              steps.splice(removeIdx, 1);
            }
            break;
          }
          case 'rename': {
            steps = steps.map(s => s.id === change.stepId ? { ...s, name: change.newName } : s);
            break;
          }
          case 'update': {
            steps = steps.map(s => s.id === change.stepId ? {
              ...s,
              ...(change.newName ? { name: change.newName } : {}),
              ...(change.newDescription ? { description: change.newDescription } : {}),
              ...(change.newType ? { type: change.newType } : {}),
            } : s);
            break;
          }
          case 'reorder': {
            const stepIdx = steps.findIndex(s => s.id === change.stepId);
            const targetPos = (change.targetPosition || 1) - 1;
            if (stepIdx >= 0 && targetPos >= 0 && targetPos < steps.length) {
              const [moved] = steps.splice(stepIdx, 1);
              steps.splice(targetPos, 0, moved);
              // Rebuild connections as simple chain
              connections = steps.slice(0, -1).map((s, i) => ({
                from: s.id, to: steps[i + 1].id,
              }));
            }
            break;
          }
          case 'split': {
            const splitIdx = steps.findIndex(s => s.id === change.stepId);
            if (splitIdx >= 0 && change.splitInto?.length >= 2) {
              const oldId = change.stepId;
              const newSteps = change.splitInto.map((sp, j) => ({
                id: `${oldId}-split-${j + 1}`,
                name: sp.name,
                description: sp.description || '',
                type: sp.type || 'transform',
                estimatedEffort: 'medium',
                isBottleneck: false,
                agentReady: false,
                agentReadyReason: '',
              }));
              // Replace in steps array
              steps.splice(splitIdx, 1, ...newSteps);
              // Fix connections
              const incoming = connections.filter(c => c.to === oldId);
              const outgoing = connections.filter(c => c.from === oldId);
              connections = connections.filter(c => c.from !== oldId && c.to !== oldId);
              // Incoming → first new step
              for (const c of incoming) connections.push({ from: c.from, to: newSteps[0].id });
              // Chain new steps
              for (let j = 0; j < newSteps.length - 1; j++) {
                connections.push({ from: newSteps[j].id, to: newSteps[j + 1].id });
              }
              // Last new step → outgoing
              for (const c of outgoing) connections.push({ from: newSteps[newSteps.length - 1].id, to: c.to });
            }
            break;
          }
          case 'merge': {
            const mergeIdx1 = steps.findIndex(s => s.id === change.stepId);
            const mergeIdx2 = steps.findIndex(s => s.id === change.mergeWithStepId);
            if (mergeIdx1 >= 0 && mergeIdx2 >= 0) {
              const keepId = change.stepId;
              const removeId = change.mergeWithStepId;
              // Update the kept step
              steps = steps.map(s => s.id === keepId ? {
                ...s,
                name: change.mergedName || s.name,
                description: change.mergedDescription || s.description,
              } : s);
              // Remove the other step
              const incoming = connections.filter(c => c.to === removeId && c.from !== keepId).map(c => c.from);
              const outgoing = connections.filter(c => c.from === removeId && c.to !== keepId).map(c => c.to);
              connections = connections.filter(c => c.from !== removeId && c.to !== removeId);
              for (const fromId of incoming) {
                if (!connections.some(c => c.from === fromId && c.to === keepId)) {
                  connections.push({ from: fromId, to: keepId });
                }
              }
              for (const toId of outgoing) {
                if (!connections.some(c => c.from === keepId && c.to === toId)) {
                  connections.push({ from: keepId, to: toId });
                }
              }
              steps = steps.filter(s => s.id !== removeId);
            }
            break;
          }
          default:
            break;
        }
      }

      return { ...prev, steps, connections };
    });

    // Update summary text if provided
    if (updatedSummaryText) {
      setSummaryData(prev => {
        if (!prev) return prev;
        return { ...prev, updatedDescription: updatedSummaryText };
      });
    }
  }, []);

  const handleStartMapping = useCallback(() => {
    setPhase(PHASES.MAPPING_LOADING);
  }, []);

  const handleMappingApiComplete = useCallback((mapping) => {
    setMappingData(mapping);
    setPhase(PHASES.MAPPING_RESULTS);
  }, []);

  const handleViewInCanvas = useCallback(() => {
    setMergedProcessData(mergeProcessData(processData, mappingData));
    setPhase(PHASES.CANVAS_FINAL);
  }, [processData, mappingData]);

  const handleNewProcess = useCallback(() => {
    setProcessData(null);
    setMappingData(null);
    setMergedProcessData(null);
    setSummaryData(null);
    setMessages([]);
    setSavedProcessId(null);
    setConversationId(null);
    creatingConversationRef.current = false;
    setPhase(PHASES.INPUT);
  }, []);

  const handleSave = useCallback(async () => {
    const data = mergedProcessData || processData;
    if (!data) return;
    const dept = departments.find((d) => d.name === selectedHA);
    const body = {
      departmentId: dept?.id || null,
      name: data.processName || 'Unbenannt',
      description: summaryData ? JSON.stringify(summaryData) : '',
      jtbd: summaryData?.jtbd
        ? `${summaryData.jtbd.verb} ${summaryData.jtbd.object} ${summaryData.jtbd.context}`
        : null,
      steps: data.steps,
      assessment: data.overallSummary,
      status: 'mapped',
    };

    try {
      if (savedProcessId) {
        await apiFetch(`${API_BASE}/api/processes/${savedProcessId}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(body),
        });
        // removed: toast 'Prozess aktualisiert'
      } else {
        const res = await apiFetch(`${API_BASE}/api/processes`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(body),
        });
        const { id } = await res.json();
        setSavedProcessId(id);
        // removed: toast 'Prozess gespeichert'
      }
    } catch (err) {
      console.error('Save error:', err);
    }
  }, [mergedProcessData, processData, summaryData, selectedHA, departments, savedProcessId]);

  const handleExport = useCallback(async () => {
    setExportState('loading');
    try {
      const res = await apiFetch(`${API_BASE}/api/export`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          processData: mergedProcessData || processData,
          mappingData,
          implementData: {},
          processName: processData?.processName,
          summaryData,
        }),
      });
      if (!res.ok) throw new Error('Export fehlgeschlagen');
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `ProcessIntelligence_${(processData?.processName || 'Export').replace(/[^a-z0-9]/gi, '_').slice(0, 50)}.html`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      setExportState('done');
      setTimeout(() => setExportState('idle'), 3000);
    } catch (err) {
      console.error('Export error:', err);
      setExportState('idle');
    }
  }, [mergedProcessData, processData, mappingData]);

  // Auto-save when entering CANVAS_FINAL
  const autoSaveRef = useRef(handleSave);
  autoSaveRef.current = handleSave;
  useEffect(() => {
    if (phase === PHASES.CANVAS_FINAL) {
      autoSaveRef.current();
    }
  }, [phase]);

  const phaseIndex = {
    [PHASES.INPUT]: 0,
    [PHASES.CONVERSATION]: 1,
    [PHASES.SUMMARY]: 2,
    [PHASES.PROCESS_CANVAS]: 3,
    [PHASES.MAPPING_LOADING]: 3,
    [PHASES.MAPPING_RESULTS]: 3,
    [PHASES.CANVAS_FINAL]: 4,
  };

  const phaseLabels = ['Eingabe', 'Gespräch', 'Zusammenfassung', 'Analyse', 'Ergebnis'];
  const currentPhaseIdx = phaseIndex[phase] ?? 0;


  return (
    <div className="app">
      <header className="app-header">
        <div className="header-brand">
          <span className="header-logo">ProcessIntelligence</span>
          <span className="header-version">MVP</span>
        </div>

        <div className="phase-indicator">
          {phaseLabels.map((label, i) => (
            <React.Fragment key={i}>
              <div
                className={`phase-step ${i === currentPhaseIdx ? 'active' : ''} ${i < currentPhaseIdx ? 'done' : ''}`}
              >
                <div className="phase-dot">
                  {i < currentPhaseIdx ? '\u2713' : i + 1}
                </div>
                <span className="phase-dot-label">{label}</span>
              </div>
              {i < phaseLabels.length - 1 && (
                <div className={`phase-connector ${i < currentPhaseIdx ? 'done' : ''}`} />
              )}
            </React.Fragment>
          ))}
        </div>

        <div className="header-actions">
          {auth.status === 'ok' && auth.user && (
            <span className="header-user" title={auth.user.email}>
              {auth.user.email}
            </span>
          )}
          {auth.status === 'ok' && auth.user && (
            <button type="button" className="btn btn-header" onClick={handleLogout}>
              Abmelden
            </button>
          )}
          {(phase === PHASES.CANVAS_FINAL || phase === PHASES.PROCESS_CANVAS) && (
            <button className="btn btn-header-primary" onClick={handleNewProcess}>
              + Neuer Prozess
            </button>
          )}
          {phase === PHASES.CANVAS_FINAL && (
            <button
              className="btn btn-generate"
              onClick={handleExport}
              disabled={exportState === 'loading'}
              title="Ergebnisse als HTML herunterladen"
              style={{
                marginLeft: '8px',
                background: exportState === 'done' ? '#2e7d32' : exportState === 'loading' ? '#999' : '#2E7C99',
                color: '#fff',
                borderColor: exportState === 'done' ? '#2e7d32' : exportState === 'loading' ? '#999' : '#2E7C99',
                cursor: exportState === 'loading' ? 'not-allowed' : 'pointer',
                transition: 'background 0.3s',
              }}
            >
              {exportState === 'loading' ? 'Wird erstellt…' : exportState === 'done' ? '✓ Heruntergeladen' : '↓ Ergebnisse exportieren'}
            </button>
          )}
        </div>
      </header>

      <main className="app-main">
        {(phase === PHASES.INPUT || phase === PHASES.CONVERSATION) && (
          <ChatPhase
            phase={phase}
            selectedHA={selectedHA}
            onHAChange={setSelectedHA}
            departments={departments}
            messages={messages}
            setMessages={setMessages}
            onStartConversation={handleStartConversation}
            onSummaryReady={handleSummaryReady}
          />
        )}

        {phase === PHASES.SUMMARY && summaryData && (
          editingTask ? (
            <div className="summary-edit-layout">
              <div className="summary-edit-main">
                <SummaryPhase
                  summary={summaryData}
                  onConfirm={handleConfirmSummary}
                  onEdit={handleEditSummary}
                  isGenerating={isGenerating}
                  error={generateError}
                />
              </div>
              <TaskEditChat
                summaryData={summaryData}
                departmentName={selectedHA || undefined}
                onApplyChanges={handleApplyTaskChanges}
                onClose={() => setEditingTask(false)}
              />
            </div>
          ) : (
            <SummaryPhase
              summary={summaryData}
              onConfirm={handleConfirmSummary}
              onEdit={handleEditSummary}
              isGenerating={isGenerating}
              error={generateError}
            />
          )
        )}

        {isGenerating && (
          <div className="loading-overlay">
            <div className="spinner" />
            <div className="loading-text">Prozess wird generiert...</div>
            <div className="loading-subtext">KI erstellt Prozessschritte basierend auf deiner Zusammenfassung</div>
          </div>
        )}

        {phase === PHASES.PROCESS_CANVAS && processData && (
          <div className="canvas-edit-layout">
            <CanvasPhase
              mode="preview"
              processData={processData}
              processContext={summaryData}
              selectedHA={selectedHA}
              mappingData={null}
              onStartMapping={handleStartMapping}
            />
            <ProcessEditChat
              processData={processData}
              summaryData={summaryData}
              onApplyChanges={handleApplyProcessChanges}
            />
          </div>
        )}

        {phase === PHASES.MAPPING_LOADING && processData && (
          <MappingLoading
            processData={processData}
            processContext={summaryData}
            onComplete={handleMappingApiComplete}
          />
        )}

        {phase === PHASES.MAPPING_RESULTS && processData && mappingData && (
          <MappingResults
            processData={processData}
            mappingData={mappingData}
            onViewInCanvas={handleViewInCanvas}
          />
        )}

        {phase === PHASES.CANVAS_FINAL && mergedProcessData && (
          <CanvasPhase
            mode="full"
            processData={mergedProcessData}
            processContext={summaryData}
            selectedHA={selectedHA}
            mappingData={mappingData}
            onExport={handleExport}
            onNewProcess={handleNewProcess}
            exportState={exportState}
          />
        )}
      </main>

      {/* Toast notification */}
      {toastMessage && (
        <div className="toast-notification">
          <span className="toast-text">{toastMessage}</span>
          <button className="toast-close" onClick={() => setToastMessage(null)}>&times;</button>
        </div>
      )}
    </div>
  );
}
